import type {
  LocalVideoTrack as BaseLocalVideoTrack,
  VideoClient as BaseVideoClient,
} from "@zoom/videosdk";
import {
  BackgroundType,
  LocalMediaStatus,
  LocalVideoContextSetter,
} from "../core/types";

/**
 * Accurate types for the {@link BaseVideoClient}. Use
 * this instead of the type from the Zoom SDK.
 */
export type VideoClient = Omit<typeof BaseVideoClient, "getCurrentUserInfo"> & {
  getCurrentUserInfo(
    ...args: Parameters<typeof BaseVideoClient.getCurrentUserInfo>
  ): ReturnType<typeof BaseVideoClient.getCurrentUserInfo> | undefined;
};

/**
 * Accurate types for the {@link BaseLocalVideoTrack}. Use
 * this instead of the type from the Zoom SDK.
 */
export interface LocalVideoTrack extends BaseLocalVideoTrack {
  isVideoStarted?: boolean;
}

type BaseStartVideoArgs = {
  setLocalVideoContext: LocalVideoContextSetter;
  successStatus: LocalMediaStatus;
};

type BlurBackgroundArgs = {
  virtualBackgroundType: BackgroundType.BLUR;
  virtualBackgroundValue: { imageUrl: "blur" };
};

type ImageBackgroundArgs = {
  virtualBackgroundType: BackgroundType.IMAGE;
  virtualBackgroundValue: {
    imageUrl: string;
  };
};

type NoBackgroundArgs = {
  virtualBackgroundType?: BackgroundType.NONE;
  virtualBackgroundValue?: undefined;
};

export type VirtualBackgroundArgs =
  | BlurBackgroundArgs
  | ImageBackgroundArgs
  | NoBackgroundArgs;

export type StartVideoArgs = BaseStartVideoArgs & VirtualBackgroundArgs;

/**
 * https://developers.zoom.us/docs/video-sdk/web/error-codes
 */
export const enum ZoomErrorCode {
  CMD_CHANNEL_NOT_READY = 7104,
}
